import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectCouponCode, selectDiscountedTripsByCoupon } from 'selectors/searchSelectors';

/**
 * Hook to access coupon code and discounted trips from Redux store.
 * Replaces prop drilling by fetching coupon data and discounted trips directly.
 *
 * Retrieves the current coupon code and its associated discounted trips from the search state.
 * Trips are stored as plain arrays; Immutable values are converted defensively.
 * Memoizes both the normalization and the entire return object.
 *
 * @returns {Object} Coupon data and discounted trips (memoized)
 * @returns {string|null} returns.couponCode - Current coupon code
 * @returns {Array} returns.discountedTrips - Array of discounted trip objects
 */
const useDiscountedTripsByCoupon = () => {
  const couponCode = useSelector(selectCouponCode);

  const rawDiscountedTrips = useSelector(selectDiscountedTripsByCoupon(couponCode));

  // Memoize normalization to a plain array to prevent unnecessary re-renders
  const discountedTrips = useMemo(() => {
    if (Array.isArray(rawDiscountedTrips)) return rawDiscountedTrips;
    if (rawDiscountedTrips && rawDiscountedTrips.toJS) return rawDiscountedTrips.toJS();
    return [];
  }, [rawDiscountedTrips]);

  // Memoize the entire return object
  return {
    couponCode,
    discountedTrips,
  };
};

export default useDiscountedTripsByCoupon;
